import axios from 'axios';
import { ErrorMessages } from './errorCodes.generated';

/**
 * Validation error from API response
 */
export interface ValidationError {
  /** Field name (camelCase) */
  field: string;
  /** Error message */
  message: string;
  /** JSON Schema instance path (e.g., "#/properties/fieldName") */
  instancePath: string;
  /** Original members array from API (may contain nested paths) */
  members: string[];
  /** Raw API error object for debugging */
  rawError?: Record<string, unknown>;
}

/**
 * The server's own human-readable error message (ABP `error.message`), for errors that are not
 * field-level validation — business exceptions, 500s. Callers use it as the fallback when
 * getValidationErrors returns nothing, instead of a generic "An error occurred".
 */
export function getApiErrorMessage(error: unknown): string | null {
  if (!axios.isAxiosError(error)) {
    return null;
  }
  const errorData = error.response?.data?.error;
  const message = errorData?.message ?? null;
  return typeof message === 'string' && message.length > 0 ? message : null;
}

/**
 * Extract validation errors from Axios error response
 * Handles ABP Framework error format and MissingRequiredFields errors
 */
/**
 * The errors a form shows for a failed request: the field-level validation errors when the
 * server sent any, else ONE form-level error carrying the server's own message (business
 * exceptions, 500s) — which beats axios's "Request failed with status code N" — and only as the
 * last resort the caller's generic fallback text.
 */
export function toFormErrors(error: unknown, fallbackMessage: string): ValidationError[] {
  const errors = getValidationErrors(error);
  if (errors.length > 0) return errors;
  return [{
    field: 'form',
    message: getApiErrorMessage(error) ?? fallbackMessage,
    instancePath: '#',
    members: [],
  }];
}

export function getValidationErrors(error: unknown): ValidationError[] {
  if (!axios.isAxiosError(error)) {
    console.log('🔍 Not an Axios error:', error);
    return [];
  }

  const errorData = error.response?.data?.error;
  if (!errorData) {
    return [];
  }

  // Handle InvalidStateTransition errors (from state machine validation)
  // Supports both legacy code "InvalidStateTransition" and new Nexo:Module:Entity:InvalidStateTransition
  if (errorData.code?.endsWith('InvalidStateTransition') && (errorData.data?.message || errorData.message)) {
    const message = errorData.data?.message || errorData.message;
    const currentStatus = errorData.data?.currentStatus || '';
    const targetStatus = errorData.data?.targetStatus || '';

    console.log('✅ Found InvalidStateTransition error:', { message, currentStatus, targetStatus });
    
    // Map to the 'status' field since state transitions are about status changes
    const { instancePath, field: fieldName } = convertMemberToInstancePath('status');
    const rawError = sanitizeErrorData(errorData);
    return [{
      field: fieldName,
      message: message,
      instancePath,
      members: ['status'],
      rawError
    }];
  }

  // Handle MissingRequiredFields errors (from policy validation)
  // Supports both legacy code "MissingRequiredFields" and new Nexo:Module:Entity:MissingRequiredFields
  if (errorData.code?.endsWith('MissingRequiredFields') && errorData.data?.fields) {
    const rawFields = errorData.data.fields;
    const fields: string[] = Array.isArray(rawFields) ? rawFields : [rawFields];
    const message = errorData.message || errorData.data.message || 'Required fields are missing';

    console.log('✅ Found MissingRequiredFields error:', { fields, message });

    const rawError = sanitizeErrorData(errorData);
    return fields.map((field: string) => {
      const { instancePath, field: fieldName } = convertMemberToInstancePath(field);
      return {
        field: fieldName,
        message: message,
        instancePath,
        members: [field],
        rawError
      };
    });
  }

  // Handle RuleValidationFailed errors (from business rules validation)
  // Supports both legacy code "RuleValidationFailed" and new Nexo:Module:Entity:RuleValidationFailed
  if (errorData.code?.endsWith('RuleValidationFailed') && errorData.data?.message) {
    const message = errorData.data.message;
    const rulesFailed = errorData.data.rulesFailed || 0;
    const rulesExecuted = errorData.data.rulesExecuted || 0;

    console.log('✅ Found RuleValidationFailed error:', { message, rulesFailed, rulesExecuted });

    // Business rule errors are general validation errors, not field-specific
    // Map to a general "form" field so they appear in the validation panel
    // The error message will be shown in the banner and panel
    const { instancePath, field: fieldName } = convertMemberToInstancePath('form');
    const rawError = sanitizeErrorData(errorData);
    return [{
      field: fieldName,
      message: message,
      instancePath,
      members: ['form'],
      rawError
    }];
  }

  // Handle known error codes using generated ErrorMessages map
  // Supports both Nexo:Module:Entity:Code (generated) and legacy short codes
  if (errorData.code) {
    // Try generated error messages first (Nexo:Module:Entity:Code pattern)
    const generatedMessage = ErrorMessages[errorData.code];
    if (generatedMessage) {
      // Use the backend-provided message if meaningful, otherwise fall back to generated default
      const message = getUsableMessage(errorData) || generatedMessage;
      console.log('✅ Found known error code:', { code: errorData.code });

      const { instancePath, field: fieldName } = convertMemberToInstancePath('form');
      const rawError = sanitizeErrorData(errorData);
      return [{
        field: fieldName,
        message,
        instancePath,
        members: ['form'],
        rawError
      }];
    }

    // Handle integration/business errors (e.g., Nexo:Sales:Order:InsufficientInventory or Sales.Order.Code)
    if (errorData.code.includes(':') || errorData.code.includes('.')) {
      const message = getUsableMessage(errorData);
      if (message) {
        console.log('✅ Found integration/business error:', { code: errorData.code, message });

        const { instancePath, field: fieldName } = convertMemberToInstancePath('form');
        const rawError = sanitizeErrorData(errorData);
        return [{
          field: fieldName,
          message,
          instancePath,
          members: ['form'],
          rawError
        }];
      }
    }
  }

  // Handle standard ABP validation errors
  const validationErrors = errorData.validationErrors;
  if (!Array.isArray(validationErrors) || validationErrors.length === 0) {
    return [];
  }

  console.log('✅ Found validation errors array:', validationErrors);

  const rawError = sanitizeErrorData(errorData);
  // ONE ValidationError per broken rule, not per field. An ABP validation error carries every field the
  // rule spans in `members` — a composite unique key ("this Plan Version + Header Type + Planning Org Unit
  // + Plan View is taken") is one violation with four members, and splitting it here printed the same
  // sentence four times in the panel. The members ride along on the single error: the panel chips them and
  // convertToJsonFormsErrors still marks every one of those inputs on the form.
  return validationErrors
    .map((err: any): ValidationError | null => {
      if (!err.message || !err.members || !Array.isArray(err.members) || err.members.length === 0) {
        return null;
      }

      // The first member anchors the error (panel grouping, scroll-to-field); the rest ride in `members`.
      const { instancePath, field } = convertMemberToInstancePath(err.members[0]);

      return {
        field,
        message: err.message,
        instancePath,
        members: err.members,
        rawError
      };
    })
    .filter(Boolean) as ValidationError[];
}

/**
 * Extract the relevant parts of the API error object for debugging display.
 * Strips null values to keep the output compact.
 */
function sanitizeErrorData(errorData: any): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const key of ['code', 'message', 'data']) {
    if (errorData[key] != null) {
      result[key] = errorData[key];
    }
  }
  return result;
}

/**
 * Extract a usable (non-generic) error message from ABP error response data.
 * ABP often masks the actual error message with "An internal error occurred during your request!"
 */
function getUsableMessage(errorData: any): string | null {
  const message = errorData.data?.message || errorData.message;
  if (!message) return null;
  if (message === 'An internal error occurred during your request!') return null;
  if (message.includes('Exception of type')) return null;
  return message;
}

/**
 * Convert API member path to JSONForms instancePath
 * Handles nested fields and array indices
 * 
 * Examples:
 * - "tags" -> { instancePath: "#/properties/tags", field: "tags" }
 * - "orderLines[0].quantity" -> { instancePath: "#/properties/orderLines/0/properties/quantity", field: "orderLines[0].quantity" }
 * - "billingAddress.street" -> { instancePath: "#/properties/billingAddress/properties/street", field: "billingAddress.street" }
 */
function convertMemberToInstancePath(member: string): { instancePath: string; field: string } {
  // Handle array indices: orderLines[0].quantity
  const arrayIndexMatch = member.match(/^(.+)\[(\d+)\](.*)$/);
  if (arrayIndexMatch) {
    const [, baseField, index, rest] = arrayIndexMatch;
    const basePath = `#/properties/${baseField}/${index}`;
    
    if (rest) {
      // Has nested path after array index
      const nestedPath = rest.startsWith('.') ? rest.substring(1) : rest;
      const nestedParts = nestedPath.split('.');
      const nestedInstancePath = nestedParts.map(part => `/properties/${part}`).join('');
      return {
        instancePath: `${basePath}${nestedInstancePath}`,
        field: member
      };
    }
    
    return {
      instancePath: basePath,
      field: member
    };
  }

  // Handle nested properties: billingAddress.street
  if (member.includes('.')) {
    const parts = member.split('.');
    const instancePath = `#/properties/${parts[0]}${parts.slice(1).map(part => `/properties/${part}`).join('')}`;
    return {
      instancePath,
      field: member
    };
  }

  // Simple field: tags
  return {
    instancePath: `#/properties/${member}`,
    field: member
  };
}

/**
 * Convert validation errors to JSONForms error format (ajv 8 ErrorObject shape)
 */
export function convertToJsonFormsErrors(validationErrors: ValidationError[]) {
  // One error may span several fields (a composite unique key names all of them), and each of those
  // inputs must be marked — so expand over `members` here, even though the panel shows the rule once.
  return validationErrors.flatMap(err => {
    const members = err.members?.length ? err.members : [err.field];
    return members.map(member => ({
      instancePath: convertMemberToInstancePath(member).instancePath,
      message: err.message,
      keyword: 'custom' as const,
      schemaPath: '#',
      params: {},
    }));
  });
}

/**
 * Get human-readable field name from field path
 * Examples:
 * - "tags" -> "Tags"
 * - "billingCity" -> "Billing City"
 * - "orderLines[0].quantity" -> "Order Lines[0] Quantity"
 */
export function getFieldDisplayName(field: string): string {
  // Handle array indices
  if (field.includes('[')) {
    const [baseField, ...rest] = field.split('[');
    const indexPart = rest.join('[');
    return `${toTitleCase(baseField)}[${indexPart}`;
  }

  // Handle nested properties
  if (field.includes('.')) {
    return field.split('.').map(part => toTitleCase(part)).join(' → ');
  }

  return toTitleCase(field);
}

/**
 * Convert camelCase to Title Case
 * Examples:
 * - "billingCity" -> "Billing City"
 * - "orderLines" -> "Order Lines"
 */
function toTitleCase(str: string): string {
  return str
    .replace(/([A-Z])/g, ' $1') // Add space before capital letters
    .replace(/^./, (char) => char.toUpperCase()) // Capitalize first letter
    .trim();
}

